import React, { useState, useEffect } from 'react';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import Layout from '../components/Layout/Layout';
import Spinner from '../components/ui/Spinner';
import { adminApi } from '../config/axios';
import { useGlobal } from '../contexts/GlobalContext';
import { dateFormat, exportToCSV, useDocumentTitle } from '../config/utils';

const DATE_RANGES = [
  { label: '7 Days',  value: 7  },
  { label: '30 Days', value: 30 },
  { label: '90 Days', value: 90 },
  { label: '1 Year',  value: 365 },
];

const CHART_OPTS = {
  plugins: { legend: { display: false } },
  scales: {
    x: { grid: { color: 'rgba(255,255,255,0.04)' }, ticks: { color: '#6B7280', font: { size: 10 } } },
    y: { grid: { color: 'rgba(255,255,255,0.04)' }, ticks: { color: '#6B7280', font: { size: 10 } } },
  },
  responsive: true, maintainAspectRatio: false,
};

export default function Reports() {
  useDocumentTitle('Reports');
  const { showToast } = useGlobal();
  const [days, setDays]           = useState(30);
  const [stats, setStats]         = useState(null);
  const [loading, setLoading]     = useState(true);
  const [revenuePerCompany, setRevenuePerCompany] = useState([]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      adminApi.get(`/reports/overview?days=${days}`).catch(() => ({ data: null })),
      adminApi.get(`/reports/revenue-per-company?days=${days}`).catch(() => ({ data: null })),
    ]).then(([statsRes, revenueRes]) => {
      setStats(statsRes.data?.data ?? {});
      setRevenuePerCompany(revenueRes.data?.data ?? []);
    }).finally(() => setLoading(false));
  }, [days]);

  const revenueData = {
    labels: stats?.revenueByDate?.map(d => dateFormat(d.date)) ?? [],
    datasets: [{
      label: 'Revenue',
      data: stats?.revenueByDate?.map(d => d.total) ?? [],
      backgroundColor: 'rgba(124,111,255,0.7)',
      borderColor: '#7C6FFF',
      borderWidth: 2,
      borderRadius: 4,
    }],
  };

  const impressionData = {
    labels: stats?.impressionsByDate?.map(d => dateFormat(d.date)) ?? [],
    datasets: [{
      label: 'Impressions',
      data: stats?.impressionsByDate?.map(d => d.count) ?? [],
      borderColor: '#00D4AA',
      backgroundColor: 'rgba(0,212,170,0.1)',
      fill: true,
      tension: 0.4,
    }],
  };

  const uptimeData = {
    labels: ['Online', 'Offline', 'Degraded'],
    datasets: [{
      data: [stats?.avgUptimeOnline ?? 0, stats?.avgUptimeOffline ?? 0, stats?.avgUptimeDegraded ?? 0],
      backgroundColor: ['rgba(0,212,170,0.8)', 'rgba(239,68,68,0.8)', 'rgba(245,166,35,0.8)'],
      borderWidth: 0,
    }],
  };

  const exportRevenue = () => {
    exportToCSV('revenue-per-company', revenuePerCompany, [
      { key: 'company_name', label: 'Company' },
      { key: 'total_revenue', label: 'Total Revenue ($)' },
      { key: 'subscription_count', label: 'Subscriptions' },
    ]);
    showToast('success', 'Exported', 'Revenue report downloaded.');
  };

  return (
    <Layout>
      <div className="page-header">
        <div>
          <h1 className="section-title text-xl">Reports</h1>
          <p className="text-text-muted text-sm mt-0.5">Platform analytics and performance data</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1 bg-bg-card border border-white/[0.08] rounded-lg p-1">
            {DATE_RANGES.map(r => (
              <button key={r.value} onClick={() => setDays(r.value)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  days === r.value ? 'bg-accent text-white' : 'text-text-muted hover:text-text-main'
                }`}>
                {r.label}
              </button>
            ))}
          </div>
          <button onClick={exportRevenue} className="btn-secondary text-xs">↓ Export CSV</button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Spinner size="lg" /></div>
      ) : (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Total Revenue',      value: `$${(stats?.totalRevenue ?? 0).toLocaleString()}`, color: 'text-teal' },
              { label: 'Ad Impressions',     value: (stats?.totalImpressions ?? 0).toLocaleString(), color: 'text-accent' },
              { label: 'Avg Device Uptime',  value: `${stats?.avgUptime ?? 0}%`, color: 'text-gold' },
              { label: 'Active Companies',   value: stats?.activeCompanies ?? 0, color: 'text-text-main' },
            ].map(card => (
              <div key={card.label} className="card-padded">
                <p className="text-xs text-text-muted uppercase tracking-wider mb-2">{card.label}</p>
                <p className={`text-2xl font-bold ${card.color}`}>{card.value}</p>
              </div>
            ))}
          </div>

          {/* Charts row 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            <div className="card-padded">
              <h3 className="section-title mb-4">Revenue Over Time</h3>
              <div className="h-52"><Bar data={revenueData} options={CHART_OPTS} /></div>
            </div>
            <div className="card-padded">
              <h3 className="section-title mb-4">Ad Impressions</h3>
              <div className="h-52"><Line data={impressionData} options={CHART_OPTS} /></div>
            </div>
          </div>

          {/* Charts row 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            <div className="card-padded flex flex-col items-center">
              <h3 className="section-title mb-4 self-start">Device Uptime</h3>
              <div className="h-44 w-44">
                <Doughnut data={uptimeData} options={{ ...CHART_OPTS, cutout: '70%',
                  plugins: { legend: { display: true, position: 'bottom', labels: { color: '#6B7280', font: { size: 10 } } } }
                }} />
              </div>
            </div>

            {/* Revenue per company */}
            <div className="lg:col-span-2 card-padded">
              <div className="flex items-center justify-between mb-4">
                <h3 className="section-title">Revenue per Company</h3>
                <button onClick={exportRevenue} className="btn-secondary text-xs">↓ CSV</button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/[0.08]">
                      <th className="table-header">Company</th>
                      <th className="table-header text-right">Revenue</th>
                      <th className="table-header text-right">Subscriptions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {revenuePerCompany.slice(0, 8).map((row, i) => (
                      <tr key={i} className="table-row">
                        <td className="table-cell">{row.company_name}</td>
                        <td className="table-cell text-right text-teal font-medium">${Number(row.total_revenue ?? 0).toFixed(2)}</td>
                        <td className="table-cell text-right">{row.subscription_count ?? 0}</td>
                      </tr>
                    ))}
                    {revenuePerCompany.length === 0 && (
                      <tr><td colSpan={3} className="table-cell text-center py-8 text-text-muted">No data available.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </Layout>
  );
}
